import { memo } from 'react';
import { Cell, Pie, PieChart, Tooltip } from 'recharts';
import type { SliceLegendEntry } from './overviewLegend';
import { percentShares, shareLabel } from './overviewSlices';
import { Swatch } from './StackedBar';

export interface PieSlice extends SliceLegendEntry {
    key: string;
    count: number;
}

const DONUT_BOX_PX = 144;
// Ring radii as a share of the box, so a smaller donut keeps the same proportions.
const INNER_RADIUS_RATIO = 46 / DONUT_BOX_PX;
const OUTER_RADIUS_RATIO = 68 / DONUT_BOX_PX;
const SLICE_GAP_DEGREES = 2;
// A small donut's tooltip outgrows its box; lift it over neighbouring charts.
const TOOLTIP_WRAPPER_STYLE = { zIndex: 10 } as const;
// Clockwise from 12 o'clock, so slices read in legend order.
const START_ANGLE = 90;
const END_ANGLE = -270;

interface DonutCenter {
    value: string;
    caption: string;
}

interface DrawnSlice extends PieSlice {
    share: string;
}

interface DonutTooltipProps {
    active?: boolean;
    payload?: { payload: DrawnSlice }[];
    centered?: boolean;
}

function DonutTooltip({ active, payload, centered = false }: DonutTooltipProps) {
    const slice = active ? payload?.[0]?.payload : undefined;
    if (!slice) return null;
    return (
        <div
            className={`flex items-center gap-2 whitespace-nowrap rounded-lg border border-gray-200 bg-white px-3 py-2 shadow-lg ${centered ? '-translate-x-1/2' : ''}`}
        >
            <Swatch className={slice.swatch} />
            <span className="text-xs font-semibold text-gray-800">{slice.label}</span>
            <span className="text-xs tabular-nums text-gray-500">
                {slice.count.toLocaleString()} · {slice.share}
            </span>
        </div>
    );
}

interface DonutChartProps {
    slices: readonly PieSlice[];
    label: string;
    center?: DonutCenter;
    /** Box width and height in px. */
    size?: number;
    /** Pins the tooltip centred under the donut, for donuts narrower than their tooltip. */
    tooltipBelow?: boolean;
}

function chartSignature({ slices, label, center, size, tooltipBelow }: DonutChartProps): string {
    const parts = slices.map((slice) => `${slice.key}:${slice.count}:${slice.fill}:${slice.label}`);
    return [label, center?.value, center?.caption, size, tooltipBelow, ...parts].join('|');
}

// Each poll brings new arrays, and recharts rebuilds its sectors for new data even when equal.
// Compare by content and skip animation, so an unchanged poll leaves the pie untouched.
export const DonutChart = memo(function DonutChart({
    slices,
    label,
    center,
    size = DONUT_BOX_PX,
    tooltipBelow = false
}: DonutChartProps) {
    const counted = slices.filter((slice) => slice.count > 0);
    const shares = percentShares(counted.map((slice) => slice.count));
    const drawn: DrawnSlice[] = counted.map((slice, index) => ({
        ...slice,
        share: shareLabel(shares[index], slice.count)
    }));
    const summary = slices.map((slice) => `${slice.label} ${slice.count}`).join(', ');
    return (
        <div
            role="img"
            aria-label={`${label}: ${summary}`}
            className="relative shrink-0"
            style={{ width: size, height: size }}
        >
            <PieChart width={size} height={size}>
                <Pie
                    data={drawn}
                    dataKey="count"
                    nameKey="label"
                    startAngle={START_ANGLE}
                    endAngle={END_ANGLE}
                    innerRadius={Math.round(size * INNER_RADIUS_RATIO)}
                    outerRadius={Math.round(size * OUTER_RADIUS_RATIO)}
                    paddingAngle={drawn.length > 1 ? SLICE_GAP_DEGREES : 0}
                    strokeWidth={0}
                    isAnimationActive={false}
                >
                    {drawn.map((slice) => (
                        <Cell key={slice.key} className={slice.fill} />
                    ))}
                </Pie>
                <Tooltip
                    content={<DonutTooltip centered={tooltipBelow} />}
                    position={tooltipBelow ? { x: size / 2, y: size } : undefined}
                    wrapperStyle={TOOLTIP_WRAPPER_STYLE}
                    isAnimationActive={false}
                />
            </PieChart>
            {center && (
                <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-lg font-bold tabular-nums text-gray-900">
                        {center.value}
                    </span>
                    <span className="text-[11px] text-gray-500">{center.caption}</span>
                </div>
            )}
        </div>
    );
}, sameChart);

function sameChart(previous: DonutChartProps, next: DonutChartProps): boolean {
    return chartSignature(previous) === chartSignature(next);
}

interface SliceLegendProps {
    slices: readonly PieSlice[];
    /** One truncated line per slice, no share column; the full label shows on hover. */
    compact?: boolean;
}

export function SliceLegend({ slices, compact = false }: SliceLegendProps) {
    const shares = percentShares(slices.map((slice) => slice.count));
    return (
        <ul className={`flex w-full min-w-0 flex-col ${compact ? 'gap-1' : 'gap-2'}`}>
            {slices.map((slice, index) => (
                <li
                    key={slice.key}
                    className={`flex items-start ${compact ? 'gap-1.5 text-[11px] leading-4' : 'gap-2.5 text-sm leading-5'}`}
                >
                    <span className={`flex items-center ${compact ? 'h-4' : 'h-5'}`}>
                        <Swatch className={slice.swatch} />
                    </span>
                    <span
                        title={compact ? slice.label : undefined}
                        className={`min-w-0 flex-1 text-gray-700 ${compact ? 'truncate' : 'break-words'}`}
                    >
                        {slice.label}
                    </span>
                    <span className="font-semibold tabular-nums text-gray-900">
                        {slice.count.toLocaleString()}
                    </span>
                    {!compact && (
                        <span className="w-10 text-right tabular-nums text-gray-500">
                            {shareLabel(shares[index], slice.count)}
                        </span>
                    )}
                </li>
            ))}
        </ul>
    );
}

export function DonutBreakdown(chart: DonutChartProps) {
    return (
        <div className="flex flex-col items-center gap-4">
            <DonutChart {...chart} />
            <SliceLegend slices={chart.slices} />
        </div>
    );
}
